import * as React from "react";
import classNames from "classnames";
import TitleLinkTop, { ITitleLinkTopProps } from "../title-link-top/title-link-top";
import TeaserTile, { ITeaserTile, TileType } from "./teaser-tile/teaser-tile";
import Button from "../button/button";
import { TeaserResponse } from "~/store/reducers/teasers/teaser-actions";
import * as teaserActions from "~/store/reducers/teasers/teaser-actions";
import teaserReducer, {
    TeaserParams,
    TeaserState,
    initialState as teaserState
} from "~/store/reducers/teasers/teaser-reducer";
import { callServer, padRootUrl } from "~/api/call-server";
import { RequestType } from "~/api/response-types/types";
import ContentSection from "../content-section/content-section";

export enum TemplateType {
    T1 = "template-1",
    T2 = "template-2",
    T3 = "template-3",
    T4 = "template-4",
    T5 = "template-5",
    T6 = "template-6"
}

const templateTypes = {
    [TemplateType.T1]: { repeats: true, pattern: [TileType.COL__1] },
    [TemplateType.T2]: { repeats: true, pattern: [TileType.COL__2] },
    [TemplateType.T3]: {
        repeats: true,
        pattern: [
            TileType.COL__2,
            TileType.COL__1,
            TileType.COL__1,
            TileType.COL__1,
            TileType.COL__1,
            TileType.COL__2
        ]
    },
    [TemplateType.T4]: {
        repeats: true,
        pattern: [
            TileType.COL__1,
            TileType.COL__1,
            TileType.COL__2,
            TileType.COL__2,
            TileType.COL__1,
            TileType.COL__1
        ]
    },
    [TemplateType.T5]: {
        repeats: false,
        pattern: [TileType.COL__2, TileType.LIST, TileType.LIST, TileType.LIST]
    },
    [TemplateType.T6]: { repeats: false, pattern: [TileType.COL__4] }
};

interface TeaserGridPropsFromStore {
    teaserListFromStore?: ITeaserTile[];
    displayedTeasersOutOfTotalLabelFromStore?: string;
    pageFromStore?: number;
    teasersTotalCountFromStore?: number;
    showLoadMoreBtn?: boolean;
    filterTags?: string[];
}

export interface ITeaserGridProps
    extends ITitleLinkTopProps,
        Omit<TeaserState, "teasers" | "teaserParams">,
        TeaserParams,
        TeaserGridPropsFromStore {
    className?: string;
    hasNoMarginTop?: boolean;
    hasNoMarginBottom?: boolean;
    title?: string;
    viewMoreLink?: string;
    templateType: TemplateType;
    teaserList?: ITeaserTile[];
    loadMoreWebApiUrl?: string;
    loadMoreBtnLabel?: string;
    anchorId?: string;
}

const TeaserGrid = ({
    className,
    title,
    viewMoreLink,
    templateType,
    loadMoreWebApiUrl,
    loadMoreBtnLabel,
    loadMorePageSize,
    filterTags,
    ...props
}: ITeaserGridProps) => {
    const baseClass = "teaser-grid";
    const componentClasses = classNames(baseClass, className, `${baseClass}--${templateType}`);

    const [state, dispatch] = React.useReducer(teaserReducer, teaserState);

    const {
        teaserList: teaserListFromStore,
        displayedTeasersOutOfTotalLabel: displayedTeasersOutOfTotalLabelFromStore,
        teaserParams: { page: pageFromStore },
        teasersTotalCount: teasersTotalCountFromStore,
        isLoadMorePending
    } = state;

    const teaserList = teaserListFromStore || props.teaserList;
    const displayedTeasersOutOfTotalLabel =
        displayedTeasersOutOfTotalLabelFromStore || props.displayedTeasersOutOfTotalLabel;
    const page = pageFromStore || props.page;
    const teasersTotalCount = teasersTotalCountFromStore || props.teasersTotalCount;
    const canLoadMore = teaserList && teasersTotalCount && teasersTotalCount > teaserList.length;
    const showLoadMoreBtn = !!(canLoadMore && loadMoreBtnLabel && props.showLoadMoreBtn);

    // Populate the store and set load-more params on mount
    React.useEffect(() => {
        const searchParams: TeaserParams = {
            page,
            loadMorePageSize,
            filterTags
        };

        dispatch(teaserActions.updateTeaserParams(searchParams));
        dispatch(teaserActions.populateTeasers({ teaserList: props.teaserList }));
    }, []);

    const onLoadMore = () => {
        if (!loadMoreWebApiUrl) {
            return;
        }

        const params = {
            ...state.teaserParams,
            apiUrl: loadMoreWebApiUrl,
            loadMorePageSize,
            // TODO: Couldn't this just be (page || 0) + 1? What happens if page is undefined?
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            page: page! + 1
        };

        dispatch(teaserActions.teaserRequest(params));

        callServer<TeaserResponse>(padRootUrl(loadMoreWebApiUrl), RequestType.POST, params)
            .then((response: any) => {
                dispatch(teaserActions.teaserSuccess({ ...response.payload, page: params.page }));
                dispatch(teaserActions.updateTeaserParams(params));
            })
            .catch(() => {
                dispatch(teaserActions.teaserError());
            });
    };
    const TeaserItem = ({
        item,
        truncateTitle,
        tileType,
        hideRichText
    }: {
        item: ITeaserTile;
        truncateTitle?: boolean;
        tileType: TileType;
        hideRichText?: boolean;
    }) => (
        <div className={`${baseClass}__item`}>
            <TeaserTile
                {...item}
                hideRichText={hideRichText}
                truncateTitle={truncateTitle}
                tileType={tileType}
            />
        </div>
    );

    const TeaserItemGroup = ({ items, tileType }: { items: ITeaserTile[]; tileType: TileType }) => (
        <div className={`${baseClass}__item-group`}>
            {items.map((item, index) => (
                <TeaserItem item={item} key={index} hideRichText truncateTitle tileType={tileType} />
            ))}
        </div>
    );

    const getTileType = (template: ITeaserGridProps["templateType"], tileIndex: number) => {
        if (teaserList?.length === 1) {
            return TileType.COL__4;
        }
        const { pattern, repeats } = templateTypes[template];

        const tileType = repeats
            ? pattern[tileIndex % pattern.length]
            : pattern[tileIndex] || TileType.COL__1;
        return tileType;
    };

    return (
        <ContentSection className={componentClasses} {...props}>
            <TitleLinkTop title={title} viewMoreLink={viewMoreLink} />
            {teaserList && (
                <>
                    <div className={`${baseClass}__items`}>
                        {templateType !== "template-5"
                            ? teaserList.map((item, index) => {
                                  return (
                                      <div key={index} className={`${baseClass}__item`}>
                                          <TeaserTile {...item} tileType={getTileType(templateType, index)} />
                                      </div>
                                  );
                              })
                            : teaserList
                                  .reduce((items: Array<ITeaserTile | ITeaserTile[]>, item, index) => {
                                      // Only items 2, 3, and 4 should be wrapped
                                      const toBuffer = index === 1 || index === 2 || index === 3;

                                      let pushTarget = items; // Where to allocate the item
                                      let pushItem: ITeaserTile | ITeaserTile[] = item; // Item to be allocated

                                      if (toBuffer) {
                                          if (Array.isArray(items[items.length - 1])) {
                                              // We already have a buffer group to allocate to
                                              pushTarget = items[items.length - 1] as ITeaserTile[];
                                          } else {
                                              // We create a new buffer group
                                              pushItem = [item];
                                          }
                                      }

                                      pushTarget.push(pushItem);

                                      return items;
                                  }, [])
                                  .map((item, index) => {
                                      if (Array.isArray(item)) {
                                          return (
                                              <TeaserItemGroup
                                                  items={item}
                                                  key={index}
                                                  tileType={TileType.LIST}
                                              />
                                          );
                                      } else if (teaserList?.length === 1) {
                                          return (
                                              <TeaserItem
                                                  item={item}
                                                  key={index}
                                                  tileType={TileType.COL__4}
                                              />
                                          );
                                      } else {
                                          return (
                                              <TeaserItem
                                                  item={item}
                                                  key={index}
                                                  tileType={TileType.COL__2}
                                              />
                                          );
                                      }
                                  })}
                    </div>
                    {displayedTeasersOutOfTotalLabel && (
                        <div className={`${baseClass}__x-of-y`}>{displayedTeasersOutOfTotalLabel}</div>
                    )}
                    {showLoadMoreBtn && (
                        <div className={`${baseClass}__button-wrapper`}>
                            <Button
                                className={`${baseClass}__button button--inverted`}
                                isLarge
                                onClick={onLoadMore}
                                disabled={isLoadMorePending}
                            >
                                {loadMoreBtnLabel}
                            </Button>
                        </div>
                    )}
                </>
            )}
        </ContentSection>
    );
};

export default TeaserGrid;
